import { Alert, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { formatPenceToPounds } from 'src/secret-menu/billing/billingUtils';
import { SummaryCard } from 'src/secret-menu/billing/SummaryCard';
import { formatPercent, percentOfStated, toArrPence, type RevenueModel } from './revenue';

export default function RevenueSummary({ revenue }: { revenue: RevenueModel | null }) {
  if (!revenue) return null;

  const { summary, potentialPence, coverage, unbilledFirms, billedWithoutUsage } = revenue;
  const loaded = percentOfStated(coverage.units, coverage.stated || null);
  const caveats: string[] = [];

  if (unbilledFirms.length > 0) {
    caveats.push(
      `${unbilledFirms.length} ${unbilledFirms.length === 1 ? 'firm has' : 'firms have'} usage but no billing configured`,
    );
  }
  if (billedWithoutUsage.length > 0) {
    caveats.push(
      `${billedWithoutUsage.length} billed ${billedWithoutUsage.length === 1 ? 'firm produces' : 'firms produce'} no usage and ${billedWithoutUsage.length === 1 ? 'is' : 'are'} left out of these totals`,
    );
  }
  if (coverage.firmsWithoutStated > 0) {
    caveats.push(
      `${coverage.firmsWithoutStated} ${coverage.firmsWithoutStated === 1 ? 'firm has' : 'firms have'} stated no client count, so ${coverage.firmsWithoutStated === 1 ? 'it is' : 'they are'} outside the loaded percentage and counted at today's bill in potential ARR`,
    );
  }

  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <SummaryCard
          label="Active MRR"
          value={formatPenceToPounds(summary.active)}
          tooltip="Firms whose billing start date has passed"
        />
        <SummaryCard
          label="Pipeline MRR"
          value={formatPenceToPounds(summary.pending)}
          tooltip="Firms configured to bill but not started yet"
        />
        <SummaryCard
          label="Projected MRR"
          value={formatPenceToPounds(summary.total)}
          tooltip="Active plus pipeline — the total once every firm is billing"
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <SummaryCard
          label="Active ARR"
          value={formatPenceToPounds(toArrPence(summary.active))}
          tooltip="Active MRR over twelve months, on the clients loaded today"
        />
        <SummaryCard
          label="Stated ARR of all clients"
          value={formatPenceToPounds(toArrPence(potentialPence))}
          tooltip="Twelve months of what every firm would bill if it loaded every client it says it has. Firms that stated no count are held at today's bill."
        />
        <SummaryCard
          label="Clients loaded"
          value={formatPercent(loaded)}
          tooltip={`Billable units against stated clients, across the ${coverage.firmsWithStated} ${coverage.firmsWithStated === 1 ? 'firm that has' : 'firms that have'} given us a figure`}
        />
      </SimpleGrid>

      {caveats.length > 0 && (
        <Alert icon={<IconInfoCircle />} variant="light" color="gray">
          <Text size="textXs">
            Revenue is a live snapshot of current units and prices, not history. A unit is a family
            or a family-less client, so loaded percentages sit below 100% for a book held in couples
            even when every client is on the platform. {caveats.join('; ')}.
          </Text>
        </Alert>
      )}
    </Stack>
  );
}
